/**
 * iter86.31 — Golf pick'em pot picker.
 *
 * Tiered pots from the OWGR pool. Default: Pot 1 (1-5, pick 1), Pot 2
 * (6-15, pick 2), Pot 3 (16-30, pick 3), Pot 4 (31+ rest of field,
 * pick 2 → 8 total). Overlap across crewmates is allowed — this is NOT
 * a draft. Editable until Round 1 tees off (backend enforces the lock).
 *
 * iter86.31.11 — Pot 4 renders as dropdown selects (large pool); pot
 * labels + counts + copy are fully dynamic from the server pot rules.
 * Also fixed: local in-progress selections no longer reset when the
 * dashboard poll refreshes `me` with identical (but new-reference) picks.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { myPicksForTournament } from "@/lib/tournamentFormat";
import { CheckCircle2, ChevronRight, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import PickSlip from "@/components/onui/PickSlip";

const NUM_WORD = { 4: "Four", 5: "Five", 6: "Six", 7: "Seven", 8: "Eight", 9: "Nine", 10: "Ten" };
const potRange = (p) => (p.rank_to >= 999 ? `${p.rank_from}+` : `${p.rank_from}–${p.rank_to}`);
const isOpenPot = (p) => p.rank_to >= 999;

export default function GolfPickPanel({ tournament, me, onPicked, hideCollapse = false, footerSlot = null }) {
    const [state, setState] = useState(null);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);
    // iter86.31.11 — Collapsible card: open while picks are incomplete,
    // collapsed once the portfolio is confirmed (or locked). User can
    // toggle anytime; collapses after a successful save.
    const [open, setOpen] = useState(null); // null = derive from saved state

    // iter86.77.18 — per-tournament picks (flat field leaks the ACTIVE
    // tournament's ids on multi-tournament leagues → "Unknown golfer").
    const myPicks = useMemo(() => myPicksForTournament(me, tournament),
        [me?.assigned_team_ids_by_tournament, me?.assigned_team_ids, tournament?.id, tournament?.teams]); // eslint-disable-line react-hooks/exhaustive-deps
    // iter86.31.11 — Content key, not reference: the dashboard reload
    // replaces `me` (~5s after mount) with a NEW array of the SAME picks,
    // which used to wipe a first-time selection in progress.
    const myPicksKey = useMemo(() => JSON.stringify([...myPicks].sort()), [myPicks]);

    useEffect(() => {
        if (!tournament?.id) return;
        api.getGolfState(tournament.id).then(setState).catch(() => {});
        // iter86.31.11 — Also refetch when the admin lock flags flip so an
        // unlock immediately re-opens picking (was: stale LOCKED badge).
    }, [tournament?.id, tournament?.golf?.last_synced_at,
        tournament?.golf?.picks_locked_by_admin, tournament?.golf?.r1_started]);

    // Sync from server only when pick CONTENT changes (mount, post-save).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setSelected(myPicks); }, [myPicksKey]);

    if (!state || state.mode !== "pickem_lowest_avg" || !me) return null;

    const locked = state.picks_locked;
    const required = state.picks_required || 8;
    const word = NUM_WORD[required] || String(required);
    const hasAllPicks = myPicks.length === required;
    if (locked && !hasAllPicks && myPicks.length === 0) {
        return (
            <div className="brutal-card p-4 text-xs text-zinc-500" data-testid="golf-picks-closed-note">
                <Lock className="w-3.5 h-3.5 inline mr-1" /> Golfer picks closed at Round 1 tee-off.
            </div>
        );
    }

    const countIn = (pot) => selected.filter(id =>
        (pot.golfers || []).some(g => g.id === id)).length;

    const toggle = (pot, golferId) => {
        if (locked) return;
        if (selected.includes(golferId)) {
            setSelected(selected.filter(id => id !== golferId));
            return;
        }
        if (countIn(pot) >= pot.picks) {
            toast.error(`Pot ${pot.pot} allows ${pot.picks} pick${pot.picks > 1 ? "s" : ""} — deselect one first`);
            return;
        }
        setSelected([...selected, golferId]);
    };

    // Dropdown slot change (open "rest of field" pot): swap oldId → newId.
    const changeSlot = (oldId, newId) => {
        if (locked) return;
        setSelected(prev => {
            let next = prev.filter(id => id !== oldId && id !== newId);
            if (newId) next = [...next, newId];
            return next;
        });
    };

    const submit = async () => {
        setSaving(true);
        try {
            const res = await api.submitGolfPicks(tournament.id, me.id, selected);
            toast.success("Golfer picks locked in!");
            if (res?.power_pick_cleared) {
                toast.info("Your Power Pick was cleared — the golfer you dropped was your Power Pick. Choose a new one from the Power Up card.");
            }
            setOpen(false);
            onPicked?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Picks failed");
        }
        setSaving(false);
    };

    const total = selected.length;
    const complete = state.pots.every(p => countIn(p) === p.picks);
    const dirty = JSON.stringify([...selected].sort()) !== myPicksKey;
    const potsCopy = state.pots
        .map(p => `${p.picks} from Pot ${p.pot} (${potRange(p)})`)
        .join(", ");
    // Derive initial open state once: collapsed when picks are already
    // confirmed (or the tournament locked with a full portfolio).
    const isOpen = hideCollapse ? true : (open === null ? !(hasAllPicks || locked) : open);

    return (
        <div className={hideCollapse ? "" : "brutal-card p-0"} data-testid="golf-pick-panel" data-open={isOpen}>
            {!hideCollapse && (
            <button
                type="button"
                onClick={() => setOpen(!isOpen)}
                data-testid="golf-pick-panel-toggle-btn"
                className="w-full p-4 flex items-center justify-between gap-2 flex-wrap rounded-t-2xl hover:bg-zinc-900/50 transition-colors"
            >
                <h3 className="font-display font-extrabold text-lg uppercase tracking-tight text-left">
                    ⛳ Your {word} — {state.event_name}
                </h3>
                <span className="flex items-center gap-2 shrink-0">
                    {locked ? (
                        <span className="pill bg-heat/20 border-heat text-heat text-[9px]" data-testid="golf-picks-locked-badge">
                            <Lock className="w-2.5 h-2.5" /> LOCKED
                        </span>
                    ) : hasAllPicks && !dirty ? (
                        <span className="pill bg-neon/15 border-neon text-neon text-[9px]" data-testid="golf-picks-saved-badge">
                            <CheckCircle2 className="w-2.5 h-2.5" /> PICKS IN
                        </span>
                    ) : (
                        <span className="pill bg-ink border-[#333] text-zinc-400 text-[10px]" data-testid="golf-picks-count">
                            {total}/{required} picked
                        </span>
                    )}
                    <ChevronRight className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </span>
            </button>
            )}

            {isOpen && (
            <div className={`space-y-4 ${hideCollapse ? "p-0" : "p-5 pt-0"}`} data-testid="golf-pick-panel-body">
            <div className="text-[11px] text-zinc-500 flex gap-2 items-start text-left">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick <b className="text-cyber">{potsCopy}</b> — world ranking tiers.
                    Crewmates CAN pick the same golfers. Lowest average strokes per round played wins — golfers who miss the cut only count their 2 rounds. Picks lock at Round 1 tee-off.
                </p>
            </div>

            {state.pots.map(pot => (
                <div key={pot.pot} data-testid={`golf-pot-${pot.pot}`}>
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                            Pot {pot.pot} · Ranks {potRange(pot)}{isOpenPot(pot) ? " · rest of field" : ""} · pick {pot.picks}
                        </p>
                        <span className={`text-[10px] font-mono ${countIn(pot) === pot.picks ? "text-neon" : "text-zinc-500"}`}>
                            {countIn(pot)}/{pot.picks}
                        </span>
                    </div>
                    {isOpenPot(pot) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {Array.from({ length: pot.picks }).map((_, i) => {
                                const potSel = selected.filter(id => (pot.golfers || []).some(g => g.id === id));
                                const val = potSel[i] || "";
                                return (
                                    <select
                                        key={i}
                                        value={val}
                                        disabled={locked}
                                        onChange={(e) => changeSlot(val, e.target.value)}
                                        data-testid={`golf-pot-${pot.pot}-slot-${i + 1}-select`}
                                        className={`w-full p-3 min-h-[48px] text-xs font-bold border rounded-lg bg-[#1A1A1A] appearance-none cursor-pointer ${
                                            val ? "border-cyber text-white" : "border-white/[0.08] text-zinc-500"
                                        } ${locked ? "cursor-default opacity-70" : "hover:border-white/25"}`}
                                    >
                                        <option value="">Golfer {i + 1} — select from rank {pot.rank_from}+…</option>
                                        {(pot.golfers || [])
                                            .filter(g => !potSel.includes(g.id) || g.id === val)
                                            .map(g => (
                                                <option key={g.id} value={g.id}>
                                                    #{g.seed} · {g.name}{g.golf?.in_field === false ? " (not in field)" : ""}
                                                </option>
                                            ))}
                                    </select>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                            {(pot.golfers || []).map(g => {
                                const on = selected.includes(g.id);
                                const notInField = g.golf?.in_field === false;
                                return (
                                    <button
                                        key={g.id}
                                        type="button"
                                        data-testid={`golf-pick-${g.id}`}
                                        onClick={() => toggle(pot, g.id)}
                                        disabled={locked}
                                        title={notInField ? "Not currently in the tournament field" : undefined}
                                        className={`relative p-3 min-h-[56px] border rounded-lg text-left transition-all ${
                                            locked ? "cursor-default opacity-70" : "cursor-pointer active:scale-[0.98]"
                                        } ${on ? "border-cyber bg-cyber/10" : "border-white/[0.08] bg-[#1A1A1A] hover:border-white/25"}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-[9px] font-mono text-zinc-500">OWGR #{g.seed}</span>
                                            {on && <CheckCircle2 className="w-3.5 h-3.5 text-cyber" />}
                                        </div>
                                        <div className="text-xs font-bold leading-tight mt-0.5 truncate">
                                            <span className="mr-1">{g.flag || "⛳"}</span>{g.name}
                                        </div>
                                        {notInField && (
                                            <span className="text-[8px] uppercase text-blaze">not in field</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}

            {/* iter86.97 — One UI Phase 3: persistent glass pick slip
                (bottom bar mobile / docked rail desktop) replaces the
                inline submit button. Live count + chips + one Submit.
                iter87.406 (user-requested) — when embedded in a modal
                that hands over its floating footer slot (PotPicksModal),
                render flush against the modal's bottom border instead. */}
            <PickSlip
                visible={!locked}
                count={total}
                required={required}
                label="golfers picked"
                chips={selected.map((id) => {
                    const g = state.pots.flatMap((p) => p.golfers || []).find((x) => x.id === id);
                    return { id, label: g?.name || "Golfer" };
                })}
                onRemoveChip={(id) => setSelected((prev) => prev.filter((x) => x !== id))}
                submitLabel={saving ? "Saving…" : hasAllPicks ? (dirty ? "Update" : "Saved ✓") : "Lock In"}
                onSubmit={submit}
                submitDisabled={!complete || saving || !dirty}
                submitTestId="golf-submit-picks-btn"
                testId="golf-pick-slip"
                portalTarget={footerSlot}
            />
            </div>
            )}
        </div>
    );
}
